import { Box, Modal, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState, useEffect } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import { selectedReportFields, selectedReportFilters, reportData } from '../../common/atom/atoms';
import SelectFieldsGenerateReportFiber from './select_fields_generate_report_fiber.component';
import ApplyFiltersGenerateReportFiber from './apply_filter_generate_report_fiber.component';
import DownloadReportCardFiber from './download_report_card_fiber.component';

export default function GenerateReportFiberSitePlanning({ open, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ['Select Fields', 'Apply Filters', 'Download'];

  const selectedFields = useAtomValue(selectedReportFields);
  const selectedFilters = useAtomValue(selectedReportFilters);
  const setSelectedFields = useSetAtom(selectedReportFields);
  const setSelectedFilters = useSetAtom(selectedReportFilters);
  const setReportData = useSetAtom(reportData);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setSelectedFields([]);
      setSelectedFilters({});
      setReportData([]);
    }
  }, [open, setSelectedFields, setSelectedFilters, setReportData]);

  const handleNext = () => {
    if (currentStep === 0 && selectedFields.length === 0) {
      alert('Please select at least one field to continue.');
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleClose = () => {
    // Clear all state when modal is closed
    setCurrentStep(0);
    setSelectedFields([]);
    setSelectedFilters({});
    setReportData([]);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'auto',
          maxHeight: '90vh',
          overflowY: 'auto',
          outline: 'none',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            background: 'var(--Misc-paper, white)',
            boxShadow: '0px 4px 18px rgba(47, 43, 61, 0.16)',
            borderRadius: 2,
            display: 'inline-flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              alignSelf: 'stretch',
              p: 3,
              display: 'inline-flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Typography
              sx={{
                color: 'rgba(47, 43, 61, 0.90)',
                fontSize: 18,
                fontFamily: 'Roboto',
                fontWeight: 500,
                wordWrap: 'break-word',
              }}
            >
              Generate Report
            </Typography>
            <Box
              sx={{ width: 24, height: 24, position: 'relative', cursor: 'pointer' }}
              onClick={handleClose}
            >
              <CloseIcon />
            </Box>
          </Box>
          {/* Stepper */}
          <Box
            sx={{
              width: '100%',
              minWidth: 600,
              height: 52,
              position: 'relative',
              background: '#F5F5F5',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '8%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                width: '84%',
              }}
            >
              {steps.map((text, index) => (
                <React.Fragment key={text}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        outline: `1px solid ${index === currentStep ? '#BD1D23' : '#ABB7C2'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: index === currentStep ? '#BD1D23' : '#ABB7C2',
                        fontSize: 14,
                        fontWeight: 500,
                        fontFamily: 'Roboto',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography
                      sx={{
                        color: index === currentStep ? '#0D0B26' : '#ABB7C2',
                        fontSize: 14,
                        fontWeight: 500,
                        fontFamily: 'Roboto',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {text}
                    </Typography>
                  </Box>
                  {index < steps.length - 1 && (
                    <Box
                      sx={{
                        flex: 1,
                        background: '#0D0B26',
                        mx: 2,
                        height: '0.6px',
                        opacity: '0.3',
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>
          {/* Step Content */}
          {currentStep === 0 && <SelectFieldsGenerateReportFiber />}
          {currentStep === 1 && <ApplyFiltersGenerateReportFiber />}
          {currentStep === 2 && <DownloadReportCardFiber />}
          {/* Footer Buttons */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              p: 2,
              mt: 'auto',
            }}
          >
            <Button onClick={handleBack} disabled={currentStep === 0} variant="outlined">
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={currentStep === steps.length - 1}
              variant="contained"
              sx={{ backgroundColor: '#BD1D23' }}
            >
              {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
